import { PageRefTypeEnum } from "../ars_ldm";
import { ARSSelect } from "../inputs/ARSSelect";
import { ARSText } from "../inputs/ARSText";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the PageRefControl component.
 */
export interface PageRefControlProps {
  /** RFC 6901 JSON Pointer path to the PageRef in ARSContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
  /** Sibling count */
  siblingCount: number;
}

/**
 * A component for managing PageRef entities.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the PageRef in ARSContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @param props.siblingCount - Number of sibling items
 * @returns A React functional component rendering the page reference editor.
 */
export const PageRefControl = ({
  dataPath,
  disabled = false,
  siblingCount,
}: PageRefControlProps): React.ReactElement => {
  const pageRefTypeOptions = Object.values(PageRefTypeEnum).map((value) => ({
    value,
    label: value,
  }));

  const idRoute = dataPath.replace(/\//g, "-");

  return (
    <div className="border rounded p-3 mb-3 position-relative">
      <div className="position-absolute top-0 end-0 m-2">
        <RemoveArsItemButton
          dataPath={dataPath}
          disabled={disabled}
          siblingCount={siblingCount}
        />
      </div>

      <ARSSelect
        dataPath={`${dataPath}/refType`}
        id={`${idRoute}-ref-type`}
        label="Reference type"
        labelWidth={2}
        options={pageRefTypeOptions}
        disabled={disabled}
      />

      <ARSText
        dataPath={`${dataPath}/label`}
        id={`${idRoute}-label`}
        label="Label"
        labelWidth={2}
        placeholder="Enter page reference label"
        disabled={disabled}
      />

      <ARSText
        dataPath={`${dataPath}/pageNames`}
        id={`${idRoute}-page-names`}
        label="Page names"
        labelWidth={2}
        placeholder="Enter one page name per line"
        isArray
        type="textarea"
        debounceMilliseconds={2000}
        rows={3}
        disabled={disabled}
      />

      <ARSText
        dataPath={`${dataPath}/pageNumbers`}
        id={`${idRoute}-page-numbers`}
        label="Page numbers"
        labelWidth={2}
        placeholder="Enter one page number per line"
        isArray
        type="textarea"
        debounceMilliseconds={2000}
        rows={3}
        disabled={disabled}
      />

      <ARSText
        dataPath={`${dataPath}/firstPage`}
        id={`${idRoute}-first-page`}
        label="First page"
        labelWidth={2}
        placeholder="Enter first page"
        disabled={disabled}
      />

      <ARSText
        dataPath={`${dataPath}/lastPage`}
        id={`${idRoute}-last-page`}
        label="Last page"
        labelWidth={2}
        placeholder="Enter last page"
        disabled={disabled}
      />
    </div>
  );
};

PageRefControl.displayName = "PageRefControl";
